import { Component, NgZone, OnInit } from '@angular/core';
import { TemplateApiService } from 'src/app/api/services/template-api.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { TemplatesService } from 'src/app/presentations/services/templates.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { environment } from 'src/environments/environment';
import { AlertsService } from '../../services/alerts.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { AlertsUpgradeModalComponent } from './alerts-upgrade-modal/alerts-upgrade-modal.component';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
  standalone: false,
})
export class AlertsComponent
  extends BaseEditableComponent<any>
  implements OnInit
{
  ALERTS_WS_URL = environment.ALERTS_WS_URL;
  showFilters: boolean = false;
  featureAvailable: boolean;
  awaitingExportResponse: boolean = false;
  templates: any[] = [];
  actions: ActionsMenu = {};

  WARNING_NOT_SUBSCRIBED = 'This feature requires an active subscription.';
  WARNING_NOT_AVAILABLE = 'This feature is not available on your current plan.';
  WARNING_MISSING_TEMPLATE = 'Please select an alerts template.';

  _alertsOn = false;
  get alertsOn(): boolean {
    return this._alertsOn;
  }
  set alertsOn(value: boolean) {
    this._alertsOn = value;
    this._updateEnabled();

    if (this.actions && this.actions.saveAction) {
      this.actions.saveAction.disabled = this.saveButtonDisabled;
    }
  }

  _termsAccepted = false;
  get termsAccepted(): boolean {
    return this._termsAccepted;
  }
  set termsAccepted(value: boolean) {
    this._termsAccepted = value;
    this._updateEnabled();

    if (this.actions && this.actions.saveAction) {
      this.actions.saveAction.disabled = this.saveButtonDisabled;
    }
  }

  get alertUrl() {
    return this.ALERTS_WS_URL + '?id=' + this.alertsService.alertKey;
  }

  allowedSendersCsv = '';
  allowedHandlingCodesCsv = '';
  allowedEventCodesCsv = '';

  constructor(
    public alertsService: AlertsService,
    private featuresService: FeaturesService,
    private templatesService: TemplatesService,
    private templateApiService: TemplateApiService,
    protected modalService: ModalService,
    private clipboardService: ClipboardService,
    public userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private ngZone: NgZone,
  ) {
    super();
  }

  get fieldsDisabled(): boolean {
    return !this.featureAvailable || !this.userStateService.hasPermission('ua');
  }

  get showCommonActions(): boolean {
    return this.hasVisibleNonCancelActions();
  }

  get saveButtonDisabled(): boolean {
    return (
      this.alertsService.isLoading ||
      !this.featureAvailable ||
      (!this.termsAccepted && this.alertsOn) ||
      (this.alertsService.missingTemplate &&
        this.alertsService.alertSettings.templateProductCode === '')
    );
  }

  get subcompanyDistributeToAllAvailable(): boolean {
    return (
      this.alertsService.alertSettings.distributeToAll &&
      !this.companyStateService.isRootCompanySelected()
    );
  }

  get isCustomPresentationSelected(): boolean {
    return !!(
      this.alertsService.alertSettings.presentationId &&
      !this.alertsService.alertSettings.templateProductCode
    );
  }

  ngOnInit(): void {
    this.alertsService.loadSettings();
    this.featureAvailable = this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_ALERTS,
    );
    if (this.alertsService.alertSettings?.enabled) {
      this._termsAccepted = true;
      this._alertsOn = true;
    }

    this.allowedSendersCsv = this.alertsService.alertSettings.allowedSenders
      ? this.alertsService.alertSettings.allowedSenders.join(',')
      : '';
    this.allowedHandlingCodesCsv = this.alertsService.alertSettings
      .allowedHandlingCodes
      ? this.alertsService.alertSettings.allowedHandlingCodes.join(',')
      : '';
    this.allowedEventCodesCsv = this.alertsService.alertSettings
      .allowedEventCodes
      ? this.alertsService.alertSettings.allowedEventCodes.join(',')
      : '';

    this.actions = {
      saveAction: {
        id: 'saveButton',
        label: 'Save',
        action: () => this.saveSettings(),
        disabled: this.saveButtonDisabled,
        requireRole: 'ua',
      },
      cancelAction: {
        id: 'cancelButton',
        label: 'Cancel',
        action: () => {
          this.bypassUnsavedChanges();
          this.stateService.go('apps.displays.list');
        },
      },
      moreOptions: [
        {
          id: 'exportButton',
          label: 'Export Alert History',
          action: () => {
            this.exportAlerts();
          },
          requireRole: 'da',
        },
      ],
    };

    this._loadTemplates();
  }

  canDeactivate(): boolean {
    return this.isDeepEqual(this.oldObject, this.alertsService.alertSettings);
  }

  private _setDefaultTemplate() {
    if (
      !this.alertsService.alertSettings.templateProductCode &&
      !this.alertsService.alertSettings.presentationId &&
      this.templates.length
    ) {
      this.alertsService.alertSettings.templateProductCode =
        this.templates[0].productCode;
    }
  }

  private async _loadTemplates() {
    if (this.templatesService.capAlerts?.length) {
      this.templates = this.templatesService.capAlerts;
    } else {
      this.alertsService.isLoading = true;
      const templatesResponse = await this.templateApiService.listTemplates({
        count: 10,
        filter: 'templateContentTypes:=Common Alert Protocol',
      });
      this.templatesService.capAlerts = templatesResponse.items;
      this.templates = templatesResponse.items;
      this.alertsService.isLoading = false;
    }

    this.oldObject = this.cloneDeep(this.alertsService.alertSettings);
    this._setDefaultTemplate();
  }

  private _updateEnabled() {
    if (this._termsAccepted && this._alertsOn) {
      this.alertsService.alertSettings.enabled = true;
    } else {
      this.alertsService.alertSettings.enabled = false;
    }
  }

  toggleAlerts() {
    if (!this.userStateService.hasPermission('ua')) {
      return;
    }

    this.alertsOn = !this.alertsOn;
    if (this.alertsOn) {
      if (!this.featureAvailable) {
        this.openUpgradeModal();
      }
      if (!this.templates.length) {
        this._loadTemplates();
      }
    }
  }

  exportAlerts() {
    const userEmail = this.userStateService.getUserEmail();

    if (!userEmail) {
      this.modalService.showMessage(
        'Error',
        'Unable to retrieve email address',
      );
      return;
    }

    const CANCELLED = 'cancelled';
    const msgPre =
      'An export file will be prepared and emailed to you at <b>' +
      userEmail +
      '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.';
    const msgPost =
      'The alert history export has been initiated. You will receive an email once it is ready.';

    return this.modalService
      .confirm('Export alerts?', msgPre, 'Export', 'Cancel')
      .catch((err) => {
        if (!err || (err instanceof Error && err.message === '')) {
          throw new Error(CANCELLED);
        }

        throw err;
      })
      .then(() => {
        this.awaitingExportResponse = true;
        return this.alertsService.exportAlerts(userEmail);
      })
      .then(() => this.modalService.showMessage('Export initiated', msgPost))
      .catch((err) => {
        if (err instanceof Error && err.message === CANCELLED) {
          return;
        }

        this.modalService.showMessage(
          'Error',
          'Unable to export at this time.',
        );
        console.error(err);
      })
      .finally(() => (this.awaitingExportResponse = false));
  }

  showUpgradePlanModal() {
    this.featuresService.showUpgradePlanModal(FeaturesService.FEATURE_ALERTS);
  }

  parseCsvList(csv: string, fieldName: string) {
    if (this.fieldsDisabled) {
      return;
    }

    if (!this.alertsService.alertSettings[fieldName]) {
      this.alertsService.alertSettings[fieldName] = [];
    }

    const list = this.alertsService.alertSettings[fieldName];
    const items = csv.trim() === '' ? [] : csv.split(',');
    list.splice(0, list.length);
    items.forEach((item) => {
      list.push(item.trim());
    });
  }

  getCheckboxList(fieldName: string, key: string) {
    return (
      this.alertsService.alertSettings[fieldName] &&
      this.alertsService.alertSettings[fieldName].indexOf(key) >= 0
    );
  }

  setCheckboxList(fieldName: string, key: string, value?: boolean) {
    if (this.fieldsDisabled) {
      return;
    }

    if (!this.alertsService.alertSettings[fieldName]) {
      this.alertsService.alertSettings[fieldName] = [];
    }

    const fieldValue = this.alertsService.alertSettings[fieldName];
    const index = fieldValue.indexOf(key);
    if ((value === undefined && index < 0) || value) {
      fieldValue.push(key);
    } else {
      fieldValue.splice(index, 1);
    }
  }

  saveSettings() {
    this.alertsService.save().then(() => {
      this.oldObject = this.cloneDeep(this.alertsService.alertSettings);
      this.scrollPageToTop();
    });
  }

  copyAlertUrl(): Promise<void> {
    return this.clipboardService.copy(this.alertUrl);
  }

  resetAlertUrl() {
    if (this.featureAvailable) {
      this.modalService
        .confirm(
          'Reset Web Service URL?',
          'If you reset your Web Service URL, you will need to provide the new URL to your CAP Alerts provider, otherwise your alerts will stop working.',
          'I Understand',
          'Cancel',
          'urlResetButton',
        )
        .then(() => {
          this.alertsService.regenerateAlertKey();
        })
        .catch(() => {});
    }
  }

  setTemplate(productCode: string) {
    if (this.fieldsDisabled) {
      return;
    }

    this.alertsService.alertSettings.presentationId = '';
    this.alertsService.alertSettings.presentationName = '';
    this.alertsService.alertSettings.templateProductCode =
      productCode || this.templates[0]?.productCode || '';
  }

  handleCustomPresentationSelection(
    event: MouseEvent | MatCheckboxChange,
  ): void {
    if (this.fieldsDisabled) {
      return;
    }

    if (event instanceof MouseEvent) {
      const target = event.target as Element;
      if (target.closest('button') || target.closest('mat-checkbox')) {
        return;
      }

      void this.setCustomPresentation(!this.isCustomPresentationSelected);
      return;
    }

    void this.setCustomPresentation(event.checked, event);
  }

  async setCustomPresentation(selected: boolean, change?: MatCheckboxChange) {
    if (selected) {
      const previousSelection = {
        templateProductCode:
          this.alertsService.alertSettings.templateProductCode,
        presentationId: this.alertsService.alertSettings.presentationId,
        presentationName: this.alertsService.alertSettings.presentationName,
      };

      this.alertsService.alertSettings.templateProductCode = '';
      this.alertsService.alertSettings.presentationId = '';
      this.alertsService.alertSettings.presentationName = '';
      const changedPresentation = await this.alertsService.changePresentation();
      if (!changedPresentation) {
        this.alertsService.alertSettings.templateProductCode =
          previousSelection.templateProductCode;
        this.alertsService.alertSettings.presentationId =
          previousSelection.presentationId;
        this.alertsService.alertSettings.presentationName =
          previousSelection.presentationName;
        if (change) {
          change.source.checked = this.isCustomPresentationSelected;
        }
      }
    } else {
      this.alertsService.alertSettings.presentationId = '';
      this.alertsService.alertSettings.presentationName = '';
      if (this.templates?.length) {
        this.alertsService.alertSettings.templateProductCode =
          this.templates[0].productCode;
      }
    }
  }

  onDefaultExpiryInput(event: any) {
    const value = parseFloat(event.target.value);
    if (value < 0 || isNaN(value)) {
      event.target.value = 0;
      this.alertsService.alertSettings.defaultExpiry = 0;
    } else {
      this.alertsService.alertSettings.defaultExpiry = value;
    }
  }

  onDefaultExpiryKeydown(event: KeyboardEvent) {
    // Prevent typing minus sign, plus sign, and 'e' (scientific notation)
    if (
      event.key === '-' ||
      event.key === '+' ||
      event.key === 'e' ||
      event.key === 'E'
    ) {
      event.preventDefault();
    }
  }

  openUpgradeModal(): void {
    this.modalService.showCustomDialog(AlertsUpgradeModalComponent, null, {
      width: '1000px',
      maxWidth: '95vw',
      panelClass: ['ai-credits-modal-panel'],
    });
  }

  isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches
    );
  }

  private scrollPageToTop(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document
      .getElementById('alerts-page-body')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private hasVisibleNonCancelActions(): boolean {
    const saveVisible =
      !!this.actions?.saveAction &&
      this.userStateService.hasPermission(this.actions.saveAction.requireRole);

    const moreOptionsVisible = this.actions?.moreOptions?.some(
      (action) =>
        !action.disabled &&
        this.userStateService.hasPermission(action.requireRole),
    );

    const deleteVisible =
      !!this.actions?.deleteAction &&
      !this.actions.deleteAction.disabled &&
      this.userStateService.hasPermission(
        this.actions.deleteAction.requireRole,
      );

    return saveVisible || moreOptionsVisible || deleteVisible;
  }
}
